"use client";

import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  location: string;
  avatar: string;
  rating: number;
}

const testimonials: Testimonial[] = [
{
  quote: "Beecon's nurse became part of our family. She cared for my mother with such warmth and professionalism — monitoring her vitals daily and keeping us fully informed. I couldn't have asked for better care.",
  name: "Amina Nakato",
  role: "Daughter of patient",
  location: "Kololo, Kampala",
  avatar: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=400&q=80",
  rating: 5
},
{
  quote: "As a busy executive, I needed reliable, professional care for my father. Beecon delivered exactly that — qualified nurses, detailed daily reports, and genuine compassion. Worth every shilling.",
  name: "Robert Ssekandi",
  role: "Family client",
  location: "Naguru, Kampala",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f9a09e33-1768451674848.png",
  rating: 5
},
{
  quote: "The wound care and medication management for my husband was exceptional. The nurse was always punctual, thorough, and so kind. Beecon gave us peace of mind during a very difficult time.",
  name: "Grace Nambooze",
  role: "Spouse of patient",
  location: "Nakasero, Kampala",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c6416dbc-1766514190378.png",
  rating: 5
}];


export default function TestimonialsSection() {
  const sectionRef = useScrollAnimation<HTMLDivElement>();

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="navy-section py-24 md:py-32"
      aria-labelledby="testimonials-heading">
      
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 reveal-up stagger-1">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase font-sans mb-4 px-4 py-2 rounded-full" style={{ color: 'var(--gold)', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)' }}>
            Families We&apos;ve Served
          </span>
          <h2 id="testimonials-heading" className="font-serif text-4xl md:text-5xl font-light text-white leading-tight mt-4 mb-5">
            Words from{" "}
            <span className="italic gold-text">Kampala Families</span>
          </h2>
          <p className="text-lg max-w-xl mx-auto leading-relaxed font-sans" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Real experiences from real families across Kololo, Naguru, and Nakasero.
          </p>
        </div>

        {/* Testimonials grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) =>
          <article
            key={`testimonial-${t.name}`}
            className={`testimonial-card p-7 reveal-up stagger-${i + 1}`}
            aria-label={`Testimonial from ${t.name}`}>
            
              {/* Stars */}
              <div className="flex gap-1 mb-5">
                {Array.from({ length: t.rating }).map((_, si) =>
              <Icon key={`star-${t.name}-${si}`} name="StarIcon" size={16} variant="solid" style={{ color: 'var(--gold)' }} />
              )}
              </div>

              {/* Quote mark */}
              <div className="font-serif text-5xl leading-none mb-4" style={{ color: 'rgba(201,168,76,0.2)' }}>&ldquo;</div>

              {/* Quote */}
              <blockquote className="font-serif text-lg font-light italic leading-relaxed text-white mb-7" style={{ color: 'rgba(255,255,255,0.82)' }}>
                {t.quote}
              </blockquote>

              {/* Author */}
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border" style={{ borderColor: 'rgba(201,168,76,0.3)' }}>
                  <AppImage
                  src={t.avatar}
                  alt={`${t.name} — ${t.role} in ${t.location}`}
                  width={44}
                  height={44}
                  className="object-cover w-full h-full" />
                
                </div>
                <div>
                  <div className="text-sm font-semibold text-white font-sans">{t.name}</div>
                  <div className="text-xs font-sans mt-0.5" style={{ color: 'rgba(201,168,76,0.65)' }}>{t.role} · {t.location}</div>
                </div>
              </div>
            </article>
          )}
        </div>
      </div>
    </section>);

}